import { defineStore } from 'pinia'
import { Travel } from '../types/travelTypes'
import { formatTime } from '../utils/dateUtils'
import { formatShortNorwegianDate } from '../utils/dateFormatter'
import type { SelectedSeat } from '../types/seatTypes'
import type { Passenger } from '../types/passengerTypes'
import { NormalizedPrice, normalizePrice } from '../utils/priceNormalization'
import { useBookingStore } from './bookingStore'
import type {
  PassengerFare,
  Ticket,
  TravelData,
  PassengerCount,
  Equipment,
  Discount,
  SelectedTickets,
  BookingData,
  UIPassengerFare,
} from '../types/myTravelTypes'

interface TravelState {
  origin: string
  destination: string
  hasReturnTravel: boolean
  outboundDate: string
  returnDate: string
  outboundTravel: Travel | null
  inboundTravel: Travel | null
  outboundTravelData: TravelData
  inboundTravelData: TravelData
  outboundPassengerCounts: Record<string, PassengerCount>
  inboundPassengerCounts: Record<string, PassengerCount>
  outboundEquipment: Equipment[]
  inboundEquipment: Equipment[]
  outboundSelectedSeats: SelectedSeat[]
  inboundSelectedSeats: SelectedSeat[]
  outboundSelectedTickets: SelectedTickets
  inboundSelectedTickets: SelectedTickets
  discounts: Discount[]
  totalPriceToBePaid: NormalizedPrice | null
  originalBookingPrice: NormalizedPrice | null
  amountAlreadyPaid: number
  isRebooking: boolean
  rebookingType: 'outbound' | 'inbound' | 'entire' | null
  outboundRebookingFee: NormalizedPrice | null
  inboundRebookingFee: NormalizedPrice | null
  outboundTotalSeatPrice: NormalizedPrice | null
  inboundTotalSeatPrice: NormalizedPrice | null
  outboundTotalEquipmentPrice: NormalizedPrice | null
  inboundTotalEquipmentPrice: NormalizedPrice | null
  originalProductPrices: {
    outbound: {
      seats: number
      equipment: number
    }
    inbound: {
      seats: number
      equipment: number
    }
  }
}

function toUIPassengerFare(
  fare: PassengerFare,
  currency = 'NOK'
): UIPassengerFare {
  return {
    passenger_id: fare.passenger_id,
    price: {
      amount: fare.price?.amount || 0,
      currency_code: fare.price?.currency_code || currency,
    },
    original_price: {
      amount: fare.original_price?.amount || 0,
      currency_code: fare.original_price?.currency_code || currency,
    },
    inventory_class: fare.inventory_class || 'STANDARD',
    tariff_code: fare.tariff_code || '',
  }
}

function toUITicket(ticket: Ticket): any {
  return {
    ...ticket,
    passenger_fares: ticket.passenger_fares?.map((fare: PassengerFare) =>
      toUIPassengerFare(fare)
    ),
  }
}

export const useMyTravelStore = defineStore('myTravel', {
  state: (): TravelState => ({
    origin: '',
    destination: '',
    hasReturnTravel: false,
    outboundDate: '',
    returnDate: '',
    outboundTravel: null,
    inboundTravel: null,
    outboundTravelData: {
      departureTime: '',
      arrivalTime: '',
      travelDuration: '',
    },
    inboundTravelData: {
      departureTime: '',
      arrivalTime: '',
      travelDuration: '',
    },
    outboundPassengerCounts: {},
    inboundPassengerCounts: {},
    outboundEquipment: [],
    inboundEquipment: [],
    outboundSelectedSeats: [],
    inboundSelectedSeats: [],
    outboundSelectedTickets: {
      tickets: [],
    },
    inboundSelectedTickets: {
      tickets: [],
    },
    discounts: [],
    totalPriceToBePaid: null,
    originalBookingPrice: null,
    amountAlreadyPaid: 0,
    isRebooking: false,
    rebookingType: null,
    outboundRebookingFee: null,
    inboundRebookingFee: null,
    outboundTotalSeatPrice: null,
    inboundTotalSeatPrice: null,
    outboundTotalEquipmentPrice: null,
    inboundTotalEquipmentPrice: null,
    // Track original product prices for rebooking price difference calculations
    originalProductPrices: {
      outbound: {
        seats: 0,
        equipment: 0,
      },
      inbound: {
        seats: 0,
        equipment: 0,
      },
    },
  }),

  hydrate(storeState) {
    this.checkAndInitializeRebooking()
  },

  getters: {
    outboundGroupedEquipment(): Equipment[] {
      return this.outboundEquipment
    },

    inboundGroupedEquipment(): Equipment[] {
      return this.inboundEquipment
    },

    totalDiscountsAmount(): number {
      if (!this.discounts || this.discounts.length === 0) {
        return 0
      }

      let totalDiscount = 0

      this.discounts.forEach((discount) => {
        if (discount?.type === 'VOUCHER') {
          totalDiscount += discount?.amount || 0
        }
      })

      return totalDiscount
    },

    isInRebookingMode(): boolean {
      return this.isRebooking
    },

    totalRebookingFee(): NormalizedPrice | null {
      if (!this.isRebooking) {
        return null
      }

      const outboundFee = this.outboundRebookingFee?.amount || 0
      const inboundFee = this.inboundRebookingFee?.amount || 0
      const result = normalizePrice(outboundFee + inboundFee, 'NOK')
      return result
    },

    outboundSeatPriceInKroner(): number {
      if (!this.outboundTotalSeatPrice) {
        return 0
      }

      let amount = this.outboundTotalSeatPrice.amount ?? 0
      const priceObj = this.outboundTotalSeatPrice as any

      if (priceObj.inOre === true) {
        amount = amount / 100
      }

      if (this.isRebooking) {
        const originalSeatPrice = this.originalProductPrices.outbound.seats || 0
        const priceDifference = amount - originalSeatPrice
        return priceDifference
      }
      return amount
    },

    inboundSeatPriceInKroner(): number {
      if (!this.inboundTotalSeatPrice) {
        return 0
      }

      let amount = this.inboundTotalSeatPrice.amount ?? 0
      const priceObj = this.inboundTotalSeatPrice as any

      if (priceObj.inOre === true) {
        amount = amount / 100
      }

      if (this.isRebooking) {
        const originalSeatPrice = this.originalProductPrices.inbound.seats || 0
        const priceDifference = amount - originalSeatPrice
        return priceDifference
      }
      return amount
    },

    outboundEquipmentPriceInKroner(): number {
      if (!this.outboundTotalEquipmentPrice) {
        return 0
      }

      let amount = this.outboundTotalEquipmentPrice.amount || 0

      if (amount > 1000) {
        amount = amount / 100
      }

      if (this.isRebooking) {
        const originalEquipmentPrice =
          this.originalProductPrices.outbound.equipment || 0
        return amount - originalEquipmentPrice
      }

      return amount
    },

    inboundEquipmentPriceInKroner(): number {
      if (!this.inboundTotalEquipmentPrice) {
        return 0
      }

      let amount = this.inboundTotalEquipmentPrice.amount ?? 0
      const priceObj = this.inboundTotalEquipmentPrice as any

      if (priceObj.inOre === true) {
        amount = amount / 100
      } else if (amount > 1000) {
        amount = amount / 100
      }

      if (this.isRebooking) {
        const originalEquipmentPrice =
          this.originalProductPrices.inbound.equipment || 0
        return amount - originalEquipmentPrice
      }

      return amount
    },

    totalSeatPriceInKroner(): number {
      const outbound = this.outboundSeatPriceInKroner
      const inbound = this.inboundSeatPriceInKroner
      const total = outbound + inbound
      return total
    },

    totalEquipmentPriceInKroner(): number {
      const outbound = this.outboundEquipmentPriceInKroner || 0
      const inbound = this.inboundEquipmentPriceInKroner || 0
      const total = outbound + inbound

      return total
    },

    rebookingPriceDifference(): NormalizedPrice {
      const defaultNormalizedPrice = {
        amount: 0,
        currency: 'NOK',
      }

      if (!this.isRebooking) {
        return defaultNormalizedPrice
      }

      const ticketPrice = this.totalPrice?.amount || 0

      const seatPriceDifference =
        this.totalSeatPriceInKroner -
        (this.originalProductPrices.outbound.seats +
          this.originalProductPrices.inbound.seats)

      const equipmentPrice = this.totalEquipmentPriceInKroner || 0

      const totalPriceDifference =
        ticketPrice + seatPriceDifference + equipmentPrice

      return {
        ...defaultNormalizedPrice,
        amount: totalPriceDifference,
      }
    },

    rebookingDisplayTotal(): NormalizedPrice {
      const defaultNormalizedPrice = {
        amount: 0,
        currency: 'NOK',
      }

      if (!this.isRebooking) {
        return this.totalPriceToBePaid || defaultNormalizedPrice
      }

      const isInitialRebookingStep =
        window.location.pathname.includes('/rebook') ||
        window.location.pathname.includes('/rebooking-search-result')

      if (isInitialRebookingStep) {
        const outboundFee = this.outboundRebookingFee?.amount || 0
        const inboundFee = this.inboundRebookingFee?.amount || 0
        const ticketPrice = this.totalPrice?.amount || 0

        const initialPrice = outboundFee + inboundFee || ticketPrice

        return {
          ...defaultNormalizedPrice,
          amount: initialPrice,
        }
      } else {
        // Calculate the full amount to be paid during rebooking (not difference)
        // This ensures consistency with PriceTag fallback calculation
        const ticketPrice = this.totalPrice?.amount || 0
        const seatPrice = this.totalSeatPriceInKroner || 0
        const equipmentPrice = this.totalEquipmentPriceInKroner || 0
        const discountAmount = this.totalDiscountsAmount || 0

        // Return the full total amount to be paid, same as PriceTag fallback logic
        const fullTotalAmount = ticketPrice + seatPrice + equipmentPrice - discountAmount
        
        return {
          ...defaultNormalizedPrice,
          amount: fullTotalAmount,
        }
      }
    },

    rebookingDisplayPrice(): NormalizedPrice {
      return this.rebookingDisplayTotal
    },

    outboundTotalPrice(): NormalizedPrice {
      const outboundTickets = this.outboundSelectedTickets?.tickets || []

      if (!outboundTickets.length) {
        return normalizePrice(0)
      }

      const total = this.outboundSelectedTickets.tickets.reduce(
        (acc: number, ticket: Ticket) => {
          let ticketPrice = 0

          if (ticket.total_price_to_be_paid) {
            ticketPrice = ticket.total_price_to_be_paid.amount || 0
          } else if (ticket.price) {
            ticketPrice = ticket.price.amount || 0
          } else if (ticket.passenger_fares?.[0]?.price) {
            ticketPrice = ticket.passenger_fares[0].price.amount || 0
          }

          if (ticket.cancelled) {
            return acc
          }

          return acc + ticketPrice
        },
        0
      )

      return normalizePrice(total)
    },

    inboundTotalPrice(): NormalizedPrice {
      const inboundTickets = this.inboundSelectedTickets?.tickets || []

      if (!inboundTickets.length) {
        return normalizePrice(0)
      }
      const total = this.inboundSelectedTickets.tickets.reduce(
        (acc: number, ticket: Ticket, index) => {
          let ticketPrice = 0

          if (ticket.total_price_to_be_paid) {
            ticketPrice = ticket.total_price_to_be_paid.amount || 0
          } else if (ticket.price) {
            ticketPrice = ticket.price.amount || 0
          } else if (ticket.passenger_fares?.[0]?.price) {
            ticketPrice = ticket.passenger_fares[0].price.amount || 0
          }

          if (ticket.cancelled) {
            return acc
          }

          return acc + ticketPrice
        },
        0
      )

      return normalizePrice(total)
    },

    totalPrice(): NormalizedPrice {
      try {
        const outbound = this.outboundTotalPrice.amount || 0
        const inbound = this.inboundTotalPrice.amount || 0

        return normalizePrice(outbound + inbound)
      } catch (error) {
        console.error('[myTravelStore] Error in totalPrice getter:', error)
        return normalizePrice(0)
      }
    },
  },

  actions: {
    /**
     * Track original product prices for rebooking price difference calculations
     * @param direction The direction (outbound or inbound)
     * @param productType The product type (seats or equipment)
     * @param totalPrice The total price of the original products
     */
    trackOriginalProductPrices(
      direction: 'outbound' | 'inbound',
      productType: 'seats' | 'equipment',
      totalPrice: number
    ) {
      if (!this.isRebooking) return

      this.originalProductPrices[direction][productType] = totalPrice
    },

    setOriginalBookingPrice(priceAmount: number) {
      this.originalBookingPrice = {
        amount: priceAmount,
        currency: 'NOK',
      }
    },

    setRebookingMode(isRebooking: boolean) {
      this.isRebooking = isRebooking
      this.saveStateToLocalStorage()
      if (isRebooking) {
        this.checkAndInitializeRebooking()
      }
    },

    checkAndInitializeRebooking() {
      if (this.isRebooking && !this.originalBookingPrice) {
        const bookingStore = useBookingStore()

        if (
          bookingStore.bookingData &&
          bookingStore.bookingData.total_price_paid
        ) {
          this.setOriginalBookingPrice(
            bookingStore.bookingData.total_price_paid
          )
        }
      }
    },

    setRebookingType(type: 'outbound' | 'inbound' | 'entire' | null) {
      this.rebookingType = type
      this.saveStateToLocalStorage()
    },

    initializeRebooking(bookingData: BookingData) {
      this.setRebookingMode(true)

      this.originalBookingPrice = {
        amount: bookingData.total_price_paid || 0,
        currency: 'NOK',
      }

      this.amountAlreadyPaid = bookingData.total_price_paid || 0

      this.outboundRebookingFee = null
      this.inboundRebookingFee = null

      if (bookingData.outbound_rebooking_fee !== undefined) {
        this.outboundRebookingFee = {
          amount: bookingData.outbound_rebooking_fee || 0,
          currency: 'NOK',
        }
      }

      if (bookingData.inbound_rebooking_fee !== undefined) {
        this.inboundRebookingFee = {
          amount: bookingData.inbound_rebooking_fee || 0,
          currency: 'NOK',
        }
      }

      this.outboundEquipment = []
      this.inboundEquipment = []
      this.outboundSeats = []
      this.inboundSeats = []
      this.discounts = []

      this.originalProductPrices = {
        outbound: {
          seats: 0,
          equipment: 0,
        },
        inbound: {
          seats: 0,
          equipment: 0,
        },
      }
    },

    setOutboundRebookingFee(feeAmount: number) {
      this.outboundRebookingFee = { amount: feeAmount, currency: 'NOK' }
    },

    setInboundRebookingFee(feeAmount: number) {
      this.inboundRebookingFee = { amount: feeAmount, currency: 'NOK' }
    },
    reset(keepRebookingContext = false) {
      const wasRebooking = this.isRebooking
      const originalPrice = this.originalBookingPrice

      this.$reset()

      this.outboundSelectedTickets = { tickets: [] }
      this.inboundSelectedTickets = { tickets: [] }

      this.outboundTravel = null
      this.inboundTravel = null

      if (keepRebookingContext && wasRebooking) {
        this.isRebooking = wasRebooking
        this.originalBookingPrice = originalPrice
      }
      this.outboundPassengerCounts = {}
      this.inboundPassengerCounts = {}
      this.originalBookingPrice = null
    },
    processTicketPrices(tickets: Ticket[]) {
      return tickets.map((ticket) => {
        const passengerFare = ticket.passenger_fares?.[0]

        const existingPrice =
          ticket.price || ticket.total_price_to_be_paid || ticket.original_price
        const passengerFarePrice = passengerFare?.price?.amount || 0
        const passengerFareOriginalPrice =
          passengerFare?.original_price?.amount || 0
        const normalizedPrice = normalizePrice(
          existingPrice || passengerFarePrice || 0,
          'NOK'
        )

        return {
          ...ticket,
          passenger_fares: ticket.passenger_fares,
          price: normalizedPrice,
          total_price_to_be_paid: normalizePrice(
            existingPrice || passengerFarePrice || 0,
            'NOK'
          ),
          original_price: normalizePrice(
            existingPrice || passengerFareOriginalPrice || 0,
            'NOK'
          ),
        }
      })
    },

    saveStateToLocalStorage() {
      try {
        const outboundTickets =
          this.outboundSelectedTickets?.tickets?.map((ticket: Ticket) => ({
            ...ticket,
            price: normalizePrice(ticket.price || 0),
            total_price_to_be_paid: normalizePrice(
              ticket.total_price_to_be_paid || ticket.price || 0
            ),
            original_price: normalizePrice(
              ticket.original_price || ticket.price || 0
            ),
          })) || []

        const inboundTickets =
          this.inboundSelectedTickets?.tickets?.map((ticket: Ticket) => ({
            ...ticket,
            price: normalizePrice(ticket.price || 0),
            total_price_to_be_paid: normalizePrice(
              ticket.total_price_to_be_paid || ticket.price || 0
            ),
            original_price: normalizePrice(
              ticket.original_price || ticket.price || 0
            ),
          })) || []

        const outboundEquipment =
          this.outboundEquipment?.map((equipment: Equipment) => {
            const normalizedEquipment = { ...equipment }

            if (normalizedEquipment.price) {
              normalizedEquipment.price = {
                amount: normalizedEquipment.price.amount || 0,
                currency:
                  normalizedEquipment.price.currency ||
                  (normalizedEquipment.price as any).currency_code ||
                  'NOK',
              }
            }

            if (
              normalizedEquipment.passenger_fares &&
              normalizedEquipment.passenger_fares.length > 0
            ) {
              normalizedEquipment.passenger_fares =
                normalizedEquipment.passenger_fares.map((fare) => {
                  const normalizedFare = { ...fare }
                  if (normalizedFare.price) {
                    normalizedFare.price = {
                      amount: normalizedFare.price.amount || 0,
                      currency:
                        normalizedFare.price.currency ||
                        (normalizedFare.price as any).currency_code ||
                        'NOK',
                    }
                  }
                  return normalizedFare
                })
            }

            return normalizedEquipment
          }) || []

        const inboundEquipment =
          this.inboundEquipment?.map((equipment: Equipment) => {
            const normalizedEquipment = { ...equipment }
            if (normalizedEquipment.price) {
              normalizedEquipment.price = {
                amount: normalizedEquipment.price.amount || 0,
                currency:
                  normalizedEquipment.price.currency ||
                  (normalizedEquipment.price as any).currency_code ||
                  'NOK',
              }
            }

            if (
              normalizedEquipment.passenger_fares &&
              normalizedEquipment.passenger_fares.length > 0
            ) {
              normalizedEquipment.passenger_fares =
                normalizedEquipment.passenger_fares.map((fare) => {
                  const normalizedFare = { ...fare }
                  if (normalizedFare.price) {
                    normalizedFare.price = {
                      amount: normalizedFare.price.amount || 0,
                      currency:
                        normalizedFare.price.currency ||
                        (normalizedFare.price as any).currency_code ||
                        'NOK',
                    }
                  }
                  return normalizedFare
                })
            }

            return normalizedEquipment
          }) || []

        const stateToSave = {
          origin: this.origin,
          destination: this.destination,
          hasReturnTravel: this.hasReturnTravel,
          outboundDate: this.outboundDate,
          returnDate: this.returnDate,
          outboundTravel: this.outboundTravel,
          inboundTravel: this.inboundTravel,
          outboundTravelData: this.outboundTravelData,
          inboundTravelData: this.inboundTravelData,
          outboundPassengerCounts: this.outboundPassengerCounts,
          inboundPassengerCounts: this.inboundPassengerCounts,
          outboundEquipment: outboundEquipment,
          inboundEquipment: inboundEquipment,
          outboundSelectedSeats: this.outboundSelectedSeats,
          inboundSelectedSeats: this.inboundSelectedSeats,
          outboundTotalSeatPrice: this.outboundTotalSeatPrice,
          inboundTotalSeatPrice: this.inboundTotalSeatPrice,
          outboundTotalEquipmentPrice: this.outboundTotalEquipmentPrice,
          inboundTotalEquipmentPrice: this.inboundTotalEquipmentPrice,
          outboundSelectedTickets: { tickets: outboundTickets },
          inboundSelectedTickets: { tickets: inboundTickets },
          discounts: this.discounts,
          totalPriceToBePaid: this.totalPriceToBePaid,
          originalBookingPrice: this.originalBookingPrice,
          amountAlreadyPaid: this.amountAlreadyPaid,
          isRebooking: this.isRebooking,
          rebookingType: this.rebookingType,
          outboundRebookingFee: this.outboundRebookingFee,
          inboundRebookingFee: this.inboundRebookingFee,
        }
        localStorage.setItem(
          'pendingMyTravelState',
          JSON.stringify(stateToSave)
        )
      } catch (error) {
        console.error('Error saving MyTravel state:', error)
      }
    },

    restoreStateFromLocalStorage() {
      try {
        const savedState = localStorage.getItem('pendingMyTravelState')
        if (savedState) {
          const state = JSON.parse(savedState)

          if (state.outboundSelectedTickets?.tickets) {
            state.outboundSelectedTickets.tickets =
              state.outboundSelectedTickets.tickets.map((ticket: Ticket) => ({
                ...ticket,
                price: normalizePrice(ticket.price || 0),
                total_price_to_be_paid: normalizePrice(
                  ticket.total_price_to_be_paid || ticket.price || 0
                ),
                original_price: normalizePrice(
                  ticket.original_price || ticket.price || 0
                ),
              }))
          }

          if (state.inboundSelectedTickets?.tickets) {
            state.inboundSelectedTickets.tickets =
              state.inboundSelectedTickets.tickets.map((ticket: Ticket) => ({
                ...ticket,
                price: normalizePrice(ticket.price || 0),
                total_price_to_be_paid: normalizePrice(
                  ticket.total_price_to_be_paid || ticket.price || 0
                ),
                original_price: normalizePrice(
                  ticket.original_price || ticket.price || 0
                ),
              }))
          }

          if (
            state.outboundGroupedEquipment &&
            Array.isArray(state.outboundGroupedEquipment)
          ) {
            state.outboundGroupedEquipment = state.outboundGroupedEquipment.map(
              (equipment: Equipment) => {
                const normalizedEquipment = { ...equipment }

                if (equipment.price && typeof equipment.price === 'object') {
                  normalizedEquipment.price = {
                    amount: equipment.price.amount || 0,
                    currency:
                      equipment.price.currency ||
                      (equipment.price as any).currency_code ||
                      'NOK',
                  }
                }

                if (
                  equipment.passenger_fares &&
                  Array.isArray(equipment.passenger_fares)
                ) {
                  normalizedEquipment.passenger_fares =
                    equipment.passenger_fares.map((fare: PassengerFare) => {
                      const normalizedFare = { ...fare }
                      if (fare.price && typeof fare.price === 'object') {
                        normalizedFare.price = {
                          amount: fare.price.amount || 0,
                          currency:
                            fare.price.currency ||
                            (fare.price as any).currency_code ||
                            'NOK',
                        }
                      }
                      return normalizedFare
                    })
                }

                return normalizedEquipment
              }
            )
          }

          if (
            state.inboundGroupedEquipment &&
            Array.isArray(state.inboundGroupedEquipment)
          ) {
            state.inboundGroupedEquipment = state.inboundGroupedEquipment.map(
              (equipment: Equipment) => {
                const normalizedEquipment = { ...equipment }
                if (equipment.price && typeof equipment.price === 'object') {
                  normalizedEquipment.price = {
                    amount: equipment.price.amount || 0,
                    currency:
                      equipment.price.currency ||
                      (equipment.price as any).currency_code ||
                      'NOK',
                  }
                }

                if (
                  equipment.passenger_fares &&
                  Array.isArray(equipment.passenger_fares)
                ) {
                  normalizedEquipment.passenger_fares =
                    equipment.passenger_fares.map((fare: PassengerFare) => {
                      const normalizedFare = { ...fare }
                      if (fare.price && typeof fare.price === 'object') {
                        normalizedFare.price = {
                          amount: fare.price.amount || 0,
                          currency:
                            fare.price.currency ||
                            (fare.price as any).currency_code ||
                            'NOK',
                        }
                      }
                      return normalizedFare
                    })
                }

                return normalizedEquipment
              }
            )
          }
          Object.assign(this, state)

          if (this.outboundEquipment?.length) {
            this.outboundTotalEquipmentPrice =
              this.calculateTotalEquipmentPrice(this.outboundEquipment)
          }
          if (this.inboundEquipment?.length) {
            this.inboundTotalEquipmentPrice = this.calculateTotalEquipmentPrice(
              this.inboundEquipment
            )
          }

          this.updateTotalPriceToBePaid()
        }
      } catch (error) {
        console.error('Error restoring MyTravel state:', error)
      }
    },

    setOriginDestination(origin: string, destination: string) {
      this.origin = origin
      this.destination = destination
    },

    setHasReturnTravel(hasReturn: boolean) {
      this.hasReturnTravel = hasReturn
    },

    setDates(outbound: string, returnDate: string | null) {      
      try {
        this.outboundDate = outbound ? formatShortNorwegianDate(outbound) : ''
        this.returnDate = returnDate ? formatShortNorwegianDate(returnDate) : ''
      } catch (error) {
        console.error('Error formatting dates:', error)
      }
    },

    setOutboundTravel(travel: Travel | null) {
      const legsLength = travel?.routes?.[0]?.legs?.length ?? 0
      const hasTransfers = legsLength > 1

      const currentTickets = this.outboundSelectedTickets?.tickets || []
      const currentEquipment = this.outboundEquipment || []
      const currentSeats = this.outboundSelectedSeats || []
      const currentSeatPrice = this.outboundTotalSeatPrice || {
        amount: 0,
        currency: 'NOK',
      }

      let sameSegments = true
      if (hasTransfers && travel?.routes?.[0]?.legs) {
        const currentSegments = this.outboundTravel?.routes?.[0]?.legs?.map(
          (leg) => leg.id
        )
        const newSegments = travel.routes[0].legs.map((leg) => leg.id)
        sameSegments =
          JSON.stringify(currentSegments) === JSON.stringify(newSegments)
      }

      this.outboundTravel = travel
      this.updateOutboundTravelData()

      if (currentTickets.length) {
        this.outboundSelectedTickets = {
          tickets: currentTickets,
        }
        const passengers = currentTickets.map((ticket: Ticket) => ({
          id:
            ticket.passenger_id ||
            `passenger_${ticket.passenger_type}_${Date.now()}`,
          type: ticket.passenger_type,
        }))
        this.updatePassengerCounts('outbound', passengers, currentTickets)
      }

      if (currentEquipment.length) {
        this.outboundEquipment = currentEquipment
      }

      if (currentSeats.length && (!hasTransfers || sameSegments)) {
        this.outboundSelectedSeats = currentSeats
        this.outboundTotalSeatPrice = currentSeatPrice
      }
      this.saveStateToLocalStorage()
    },

    setInboundTravel(travel: Travel | null) {
      const legsLength = travel?.routes?.[0]?.legs?.length ?? 0
      const hasTransfers = legsLength > 1
      const currentTickets = this.inboundSelectedTickets?.tickets || []
      const currentEquipment = this.inboundEquipment || []
      const currentSeats = this.inboundSelectedSeats || []
      const currentSeatPrice = this.inboundTotalSeatPrice || {
        amount: 0,
        currency: 'NOK',
      }

      let sameSegments = true
      if (hasTransfers && travel?.routes?.[0]?.legs) {
        const currentSegments = this.inboundTravel?.routes?.[0]?.legs?.map(
          (leg) => leg.id
        )
        const newSegments = travel.routes[0].legs.map((leg) => leg.id)
        sameSegments =
          JSON.stringify(currentSegments) === JSON.stringify(newSegments)
      }

      this.inboundTravel = travel
      this.updateInboundTravelData()
      if (currentTickets.length) {
        this.inboundSelectedTickets = {
          tickets: currentTickets,
        }
        const passengers = currentTickets.map((ticket: Ticket) => ({
          id:
            ticket.passenger_id ||
            `passenger_${ticket.passenger_type}_${Date.now()}`,
          type: ticket.passenger_type,
        }))
        this.updatePassengerCounts('inbound', passengers, currentTickets)
      }

      if (currentEquipment.length) {
        this.inboundEquipment = currentEquipment
      }

      if (currentSeats.length && (!hasTransfers || sameSegments)) {
        this.inboundSelectedSeats = currentSeats
        this.inboundTotalSeatPrice = currentSeatPrice
      }

      this.saveStateToLocalStorage()
    },

    calculateTotalSeatPrice(seats: SelectedSeat[]) {
      let hasExplicitInOreFlag = false
      let explicitInOreValue = false

      seats.forEach((seat) => {
        if (
          typeof seat.price === 'object' &&
          seat.price !== null &&
          'inOre' in seat.price
        ) {
          hasExplicitInOreFlag = true
          explicitInOreValue = Boolean(seat.price.inOre)
        }
      })

      const totalPriceRaw = seats.reduce((total, seat) => {
        const seatPrice =
          typeof seat.price === 'number'
            ? seat.price
            : typeof seat.price?.amount === 'number'
            ? seat.price.amount
            : 0

        const inOreFlag = typeof seat.price === 'object' && seat.price?.inOre
        return total + seatPrice
      }, 0)

      let isInOre = hasExplicitInOreFlag
        ? explicitInOreValue
        : totalPriceRaw > 1000

      if (this.isRebooking) {
        isInOre = true
      }

      return {
        amount: totalPriceRaw,
        currency: 'NOK',
        inOre: isInOre,
      } as NormalizedPrice & { inOre: boolean }
    },

    /**
     * Track original product prices for rebooking price difference calculations
     * @param direction Direction (outbound or inbound)
     * @param productType Type of product (seats or equipment)
     * @param totalPrice Total price of the original products
     */
    trackOriginalProductPrices(
      direction: 'outbound' | 'inbound',
      productType: 'seats' | 'equipment',
      totalPrice: number
    ) {
      if (!this.isRebooking) return

      this.originalProductPrices[direction][productType] = totalPrice
    },

    updateTotalPriceToBePaid() {
      if (this.isRebooking) {
        const recalculatedTotal = this.rebookingDisplayTotal
        this.totalPriceToBePaid = recalculatedTotal
        return
      }

      const ticketPrice = this.totalPrice?.amount ?? 0
      const seatPrice = this.totalSeatPriceInKroner || 0
      const equipmentPrice = this.totalEquipmentPriceInKroner || 0
      const discountAmount = this.totalDiscountsAmount || 0

      const baseTotal = ticketPrice + seatPrice + equipmentPrice
      const totalAfterDiscounts = baseTotal - discountAmount

      this.totalPriceToBePaid = {
        amount: totalAfterDiscounts,
        currency: 'NOK',
      }
    },

    updateDiscountsAndTotal(bookingData: BookingData) {
      const allDiscounts: any[] = []

      if (bookingData.vouchers?.length) {
        const voucherDiscounts = bookingData.vouchers.map((voucher: any) => ({
          name: voucher.description || voucher.alias || 'Voucher',
          code: voucher.voucher_code || voucher.code,
          amount: voucher.amount || 0,
          percentage: voucher.percentage || 0,
          type: voucher.type || 'VOUCHER',
          voucher_id: voucher.id,
          provisional: voucher.active !== false,
          cancelled: voucher.active === false,
          currency: voucher.currency || 'NOK',
        }))

        allDiscounts.push(...voucherDiscounts)
      }

      const allSegments = [
        ...(bookingData.outbound_booking_tariff_segments || []),
        ...(bookingData.inbound_booking_tariff_segments || []),
      ]
      const segmentDiscounts: any[] = []

      allSegments.forEach((segment: any) => {
        if (segment.required_products?.length) {
          segment.required_products.forEach((product: any) => {
            if (product.discounts?.length) {
              segmentDiscounts.push(...product.discounts)
            }
          })
        }

        if (segment.additional_products?.length) {
          segment.additional_products.forEach((product: any) => {
            if (product.discounts?.length) {
              segmentDiscounts.push(...product.discounts)
            }
          })
        }
      })

      if (segmentDiscounts.length) {
        const processedSegmentDiscounts = segmentDiscounts.map(
          (discount: any) => ({
            name: discount.name || 'Discount',
            code: discount.code || '',
            amount: 0,
            percentage: 0,
            type: discount.type || 'DISCOUNT',
            voucher_id: discount.voucher_id || '',
            provisional: discount.provisional !== false,
            cancelled: discount.cancelled === true,
            currency: discount.currency || 'NOK',
          })
        )

        allDiscounts.push(...processedSegmentDiscounts)
      }
      this.discounts = allDiscounts
      let totalPriceToUse = null
      if (bookingData.total_price_to_be_paid) {
        totalPriceToUse = bookingData.total_price_to_be_paid
      } else if (bookingData.total_price) {
        totalPriceToUse = bookingData.total_price
      }

      if (totalPriceToUse) {
        let amount =
          typeof totalPriceToUse === 'object'
            ? totalPriceToUse.amount || 0
            : typeof totalPriceToUse === 'number'
            ? totalPriceToUse
            : 0

        const currency =
          (typeof totalPriceToUse === 'object' && totalPriceToUse.currency) ||
          (typeof totalPriceToUse === 'object' &&
            totalPriceToUse.currency_code) ||
          'NOK'

        const voucherDiscountAmount = this.totalDiscountsAmount
        amount = amount - voucherDiscountAmount

        this.totalPriceToBePaid = {
          amount: amount,
          currency: currency,
        }
      } else {
        if (bookingData.tickets?.length) {
          const outboundTickets: Ticket[] = []
          const inboundTickets: Ticket[] = []

          bookingData.tickets.forEach((ticket: any) => {
            if (ticket.status === 'CANCELLED') return

            if (ticket.direction === 'OUTBOUND') {
              outboundTickets.push(ticket)
            } else if (ticket.direction === 'INBOUND') {
              inboundTickets.push(ticket)
            }
          })

          if (outboundTickets.length) {
            this.updateSelectedTickets('outbound', outboundTickets)
          }

          if (inboundTickets.length) {
            this.updateSelectedTickets('inbound', inboundTickets)
          }
        }

        this.updateTotalPriceToBePaid()
      }
    },

    updateSelectedTickets(
      direction: 'outbound' | 'inbound',
      tickets: Ticket[]
    ) {
      const processedTickets = tickets.map((ticket: Ticket) => {
        const priceValue = ticket.price?.amount || 0
        const totalPriceValue =
          ticket.total_price_to_be_paid?.amount || ticket.price?.amount || 0
        const originalPriceValue =
          ticket.original_price?.amount || ticket.price?.amount || 0

        const newPrice = {
          amount: priceValue,
          currency: 'NOK',
        }

        const newTotalPrice = {
          amount: totalPriceValue,
          currency: 'NOK',
        }

        const newOriginalPrice = {
          amount: originalPriceValue,
          currency: 'NOK',
        }

        if (priceValue === 0 && ticket.product_family) {
          if (
            direction === 'outbound' &&
            this.outboundTravel?.bundles?.[0]?.price
          ) {
            newPrice.amount = this.outboundTravel.bundles[0].price
            newTotalPrice.amount = newPrice.amount
          } else if (
            direction === 'inbound' &&
            this.inboundTravel?.bundles?.[0]?.price
          ) {
            newPrice.amount = this.inboundTravel.bundles[0].price
            newTotalPrice.amount = newPrice.amount
          }
        }

        const passengerTypeDisplay =
          ticket.passenger_type === 'ADT' ? 'Voksen' : ticket.passenger_type
        const ticketName =
          ticket.ticket_name ||
          `${passengerTypeDisplay} - ${ticket.product_family || ''}`

        const apiTicket = {
          ...ticket,
          passenger_fares: ticket.passenger_fares,
          price: newPrice,
          total_price_to_be_paid: newTotalPrice,
          original_price: newOriginalPrice,
          ticket_name: ticketName,
        }

        return toUITicket(apiTicket)
      })

      const existingTickets =
        direction === 'outbound'
          ? this.outboundSelectedTickets?.tickets || []
          : this.inboundSelectedTickets?.tickets || []

      const ticketsByPassengerId = new Map()

      existingTickets.forEach((ticket: Ticket) => {
        if (ticket.passenger_id) {
          ticketsByPassengerId.set(ticket.passenger_id, ticket)
        }
      })

      processedTickets.forEach((newTicket: Ticket) => {
        if (newTicket.passenger_id) {
          const existingTicket = ticketsByPassengerId.get(
            newTicket.passenger_id
          )

          if (existingTicket) {
            const mergedTicket = {
              ...existingTicket,
              ...newTicket,
              price:
                newTicket.price?.amount === 0 && existingTicket.price?.amount
                  ? existingTicket.price
                  : newTicket.price,
              total_price_to_be_paid:
                newTicket.total_price_to_be_paid?.amount === 0 &&
                existingTicket.total_price_to_be_paid?.amount
                  ? existingTicket.total_price_to_be_paid
                  : newTicket.total_price_to_be_paid,
              passenger_fares: newTicket.passenger_fares?.length
                ? newTicket.passenger_fares
                : existingTicket.passenger_fares,
            }

            ticketsByPassengerId.set(newTicket.passenger_id, mergedTicket)
          } else {
            ticketsByPassengerId.set(newTicket.passenger_id, newTicket)
          }
        }
      })

      const mergedTickets = Array.from(ticketsByPassengerId.values())

      if (direction === 'outbound') {
        this.outboundSelectedTickets = {
          tickets: mergedTickets,
        }
      } else {
        this.inboundSelectedTickets = {
          tickets: mergedTickets,
        }
      }
      this.updateTotalPriceToBePaid()
      this.saveStateToLocalStorage()
    },

    updatePassengerCounts(
      direction: 'outbound' | 'inbound',
      passengers: Passenger[],
      tickets: Ticket[]
    ) {
      const existingCounts =
        direction === 'outbound'
          ? { ...this.outboundPassengerCounts }
          : { ...this.inboundPassengerCounts }

      const counts: Record<string, PassengerCount> = {}

      passengers.forEach((passenger) => {
        counts[passenger.type] = {
          count: 0,
          price: null,
          name: '',
          product_family: '',
        }
      })

      const ticketsByType: Record<string, Ticket[]> = {}

      tickets.forEach((ticket) => {
        const type = ticket.passenger_type

        if (!ticketsByType[type]) {
          ticketsByType[type] = []
        }

        ticketsByType[type].push(ticket)

        if (!counts[type]) {
          counts[type] = {
            count: 0,
            price: null,
            name: '',
            product_family: ticket.product_family || '',
          }
        }

        counts[type].count++
      })

      Object.entries(ticketsByType).forEach(([type, typeTickets]) => {
        let totalPrice = 0

        typeTickets.forEach((ticket) => {
          if (ticket.price?.amount) {
            totalPrice += ticket.price.amount
          } else if (ticket.total_price_to_be_paid?.amount) {
            totalPrice += ticket.total_price_to_be_paid.amount
          }
        })

        const normalizedPrice = normalizePrice(
          {
            amount: totalPrice,
            currency_code: 'NOK',
          },
          'NOK'
        )

        const commonTypeMap: Record<string, string> = {
          ADT: 'Voksen',
          CHD: 'Barn',
          YOU: 'Ungdom',
          STU: 'Student',
          SEN: 'Senior',
          CON: 'Vernepliktig',
          CPN: 'Ledsager',
          COM: 'Ledsager',
        }

        const passengerTypeTranslation = commonTypeMap[type] || type
        const firstTicket = typeTickets[0]
        const productFamily = firstTicket.product_family || ''
        const ticketName =
          firstTicket.ticket_name ||
          `${passengerTypeTranslation} - ${productFamily}`
        const existingCount = existingCounts[type]

        if (existingCount) {
          const shouldKeepExistingData =
            ((!firstTicket.product_family ||
              firstTicket.product_family === '') &&
              existingCount.product_family) ||
            ((!normalizedPrice.amount || normalizedPrice.amount === 0) &&
              existingCount.price &&
              existingCount.price.amount > 0)

          if (shouldKeepExistingData) {
            counts[type].price = existingCount.price
            counts[type].name = existingCount.name
            counts[type].product_family = existingCount.product_family
            return
          }
        }
        counts[type].price = normalizedPrice
        counts[type].name = ticketName
        counts[type].product_family = firstTicket.product_family || ''
      })

      if (direction === 'outbound') {
        this.outboundPassengerCounts = counts
      } else {
        this.inboundPassengerCounts = counts
      }
    },

    calculateTotalEquipmentPrice(equipment: Equipment[]): NormalizedPrice {
      let totalAmount = 0

      equipment.forEach((item) => {
        const count = item.count || 1
        const price = item.price?.amount || 0
        const itemTotal = count * price
        totalAmount += itemTotal
      })

      return {
        amount: totalAmount,
        currency: 'NOK',
      }
    },

    updateGroupedEquipment(
      direction: 'outbound' | 'inbound',
      equipment: Equipment[],
      replaceExisting: boolean = true
    ) {
      const validEquipment = equipment.filter((item) => {
        const isCancelled =
          item.cancelled === true ||
          item.cancelled === 'true' ||
          item.cancelled === 1 ||
          item.cancelled === '1'
        return !isCancelled
      })

      let groupedEquipment: Record<string, Equipment> = {}

      if (!replaceExisting) {
        const currentEquipment =
          direction === 'outbound'
            ? this.outboundEquipment || []
            : this.inboundEquipment || []

        const validCurrentItems = currentEquipment.filter((item) => {
          const isCancelled =
            item.cancelled === true || item.cancelled === 'true'
          return !isCancelled
        })

        validCurrentItems.forEach((item) => {
          if (item.product_code) {
            groupedEquipment[item.product_code] = { ...item }
          }
        })
      }

      validEquipment.forEach((item) => {
        if (item.product_code) {
          const key = item.product_code
          if (!groupedEquipment[key]) {
            const normalizedItem = { ...item }

            if (
              normalizedItem.price?.amount &&
              normalizedItem.price.amount > 1000
            ) {
              normalizedItem.price.amount = normalizedItem.price.amount / 100
            }

            if (
              normalizedItem.passenger_fares &&
              Array.isArray(normalizedItem.passenger_fares)
            ) {
              normalizedItem.passenger_fares =
                normalizedItem.passenger_fares.map((fare) => {
                  const normalizedFare = { ...fare }
                  if (
                    normalizedFare.price?.amount &&
                    normalizedFare.price.amount > 1000
                  ) {
                    normalizedFare.price.amount =
                      normalizedFare.price.amount / 100
                  }
                  return normalizedFare
                })
            }

            groupedEquipment[key] = normalizedItem
          } else if (replaceExisting) {
            const oldCount = groupedEquipment[key].count
            groupedEquipment[key].count += item.count
          }
        }
      })

      const processedEquipment = Object.values(groupedEquipment)
      const totalPrice = this.calculateTotalEquipmentPrice(processedEquipment)

      if (direction === 'outbound') {
        this.outboundEquipment = processedEquipment
        this.outboundTotalEquipmentPrice = totalPrice
      } else {
        this.inboundEquipment = processedEquipment
        this.inboundTotalEquipmentPrice = totalPrice
      }

      this.recalculateAllPrices()
      this.saveStateToLocalStorage()
    },

    updateSelectedSeats(
      direction: 'outbound' | 'inbound',
      seats: SelectedSeat[],
      segmentIds?: string[]
    ) {
      const currentSeats =
        direction === 'outbound'
          ? this.outboundSelectedSeats
          : this.inboundSelectedSeats

      if (seats.length === 0 && currentSeats.length > 0) {
        const currentSegmentsByLegIndex = new Map<number, Set<string>>()

        currentSeats.forEach((seat) => {
          if (seat.legIndex !== undefined) {
            if (!currentSegmentsByLegIndex.has(seat.legIndex)) {
              currentSegmentsByLegIndex.set(seat.legIndex, new Set<string>())
            }
            if (seat.segmentId) {
              currentSegmentsByLegIndex.get(seat.legIndex)?.add(seat.segmentId)
            }
          }
        })

        let legIndexToRemove: number | undefined
        const firstCurrentSeat = seats[0] || currentSeats[0]
        if (firstCurrentSeat && firstCurrentSeat.legIndex !== undefined) {
          legIndexToRemove = firstCurrentSeat.legIndex
        }

        if (legIndexToRemove !== undefined) {
          const updatedSeats = currentSeats.filter(
            (seat) => seat.legIndex !== legIndexToRemove
          )

          const updatedTotalPrice = this.calculateTotalSeatPrice(updatedSeats)

          if (direction === 'outbound') {
            this.outboundSelectedSeats = updatedSeats
            this.outboundTotalSeatPrice = updatedTotalPrice
          } else {
            this.inboundSelectedSeats = updatedSeats
            this.inboundTotalSeatPrice = updatedTotalPrice
          }

          this.updateTotalPriceToBePaid()
          this.saveStateToLocalStorage()
          return
        }
      }

      const seatsByPassenger = new Map<string, SelectedSeat[]>()
      currentSeats.forEach((seat) => {
        if (seat.passenger_id) {
          const passengerSeats = seatsByPassenger.get(seat.passenger_id) || []
          passengerSeats.push(seat)
          seatsByPassenger.set(seat.passenger_id, passengerSeats)
        }
      })

      const seatsBySegment = new Map<string, SelectedSeat[]>()
      seats.forEach((seat) => {
        if (seat.segmentId) {
          const segmentSeats = seatsBySegment.get(seat.segmentId) || []
          segmentSeats.push(seat)
          seatsBySegment.set(seat.segmentId, segmentSeats)
        }
      })

      const unchangedSegments = new Set<string>()
      currentSeats.forEach((seat) => {
        if (seat.segmentId && !seatsBySegment.has(seat.segmentId)) {
          unchangedSegments.add(seat.segmentId)
        }
      })

      const unchangedSeats = currentSeats.filter(
        (seat) => seat.segmentId && unchangedSegments.has(seat.segmentId)
      )

      const updatedSeats = [...seats, ...unchangedSeats]
      const updatedTotalPrice = this.calculateTotalSeatPrice(updatedSeats)

      if (direction === 'outbound') {
        this.outboundSelectedSeats = updatedSeats
        this.outboundTotalSeatPrice = updatedTotalPrice
      } else {
        this.inboundSelectedSeats = updatedSeats
        this.inboundTotalSeatPrice = updatedTotalPrice
      }

      this.updateTotalPriceToBePaid()
      this.saveStateToLocalStorage()
    },

    updateOutboundTravelData() {
      if (!this.outboundTravel) return

      if (this.outboundSelectedTickets?.tickets?.length) {
        const firstTicket = this.outboundSelectedTickets.tickets[0]
        if (
          firstTicket?.departure_timestamp &&
          firstTicket?.arrival_timestamp
        ) {
          const departureTime = formatTime(firstTicket.departure_timestamp)
          const arrivalTime = formatTime(firstTicket.arrival_timestamp)

          const start = new Date(firstTicket.departure_timestamp)
          const end = new Date(firstTicket.arrival_timestamp)
          const durationInMinutes =
            (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.outboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
          return
        }
      }

      const route = this.outboundTravel.routes?.[0]
      if (route?.legs?.[0]) {
        const leg = route.legs[0]
        const departureTimestamp = leg.departure_station.departure_timestamp
        const arrivalTimestamp = leg.arrival_station.arrival_timestamp

        if (departureTimestamp && arrivalTimestamp) {
          const departureTime = formatTime(departureTimestamp)
          const arrivalTime = formatTime(arrivalTimestamp)

          const start = new Date(departureTimestamp)
          const end = new Date(arrivalTimestamp)
          const durationInMinutes =
            (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.outboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
        }
      }
    },

    updateInboundTravelData() {
      if (!this.inboundTravel) return

      if (this.inboundSelectedTickets?.tickets?.length) {
        const firstTicket = this.inboundSelectedTickets.tickets[0]
        if (
          firstTicket?.departure_timestamp &&
          firstTicket?.arrival_timestamp
        ) {
          const departureTime = formatTime(firstTicket.departure_timestamp)
          const arrivalTime = formatTime(firstTicket.arrival_timestamp)

          const start = new Date(firstTicket.departure_timestamp)
          const end = new Date(firstTicket.arrival_timestamp)
          const durationInMinutes =
            (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.inboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
          return
        }
      }

      const route = this.inboundTravel.routes?.[0]
      if (route?.legs?.[0]) {
        const leg = route.legs[0]
        const departureTimestamp = leg.departure_station.departure_timestamp
        const arrivalTimestamp = leg.arrival_station.arrival_timestamp

        if (departureTimestamp && arrivalTimestamp) {
          const departureTime = formatTime(departureTimestamp)
          const arrivalTime = formatTime(arrivalTimestamp)

          const start = new Date(departureTimestamp)
          const end = new Date(arrivalTimestamp)
          const durationInMinutes =
            (end.getTime() - start.getTime()) / (1000 * 60)
          const hours = Math.floor(durationInMinutes / 60)
          const minutes = Math.floor(durationInMinutes % 60)
          const travelDuration = `${hours}t ${minutes}min`

          this.inboundTravelData = {
            departureTime,
            arrivalTime,
            travelDuration,
          }
        }
      }
    },

    recalculateAllPrices() {
      if (this.outboundEquipment && this.outboundEquipment.length > 0) {
        this.outboundTotalEquipmentPrice = this.calculateTotalEquipmentPrice(
          this.outboundEquipment
        )
      }

      if (this.inboundEquipment && this.inboundEquipment.length > 0) {
        this.inboundTotalEquipmentPrice = this.calculateTotalEquipmentPrice(
          this.inboundEquipment
        )
      }

      if (this.outboundSelectedSeats && this.outboundSelectedSeats.length > 0) {
        const outboundSeatPrice = this.calculateTotalSeatPrice(
          this.outboundSelectedSeats
        )
        this.outboundTotalSeatPrice = outboundSeatPrice
      }

      if (this.inboundSelectedSeats && this.inboundSelectedSeats.length > 0) {
        const inboundSeatPrice = this.calculateTotalSeatPrice(
          this.inboundSelectedSeats
        )
        this.inboundTotalSeatPrice = inboundSeatPrice
      }

      this.updateTotalPriceToBePaid()
    },
  },
})
